<script setup lang="ts">
import { type Component, computed } from 'vue';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';

import {
  type SnackbarIcon,
  type SnackbarIconBinding,
} from '@/core/shared/snackbar/types/snackbar.type';

import CloseIcon from '@/assets/svg/conversations/CloseIcon.svg?component';
import TrashIcon from '@/assets/svg/conversations/TrashIcon.svg?component';
import PenIcon from '@/assets/svg/conversations/PenIcon.svg?component';
import AlertCircleIcon from '@/assets/svg/conversations/alert-circle.svg?component';
import AlertTriangleIcon from '@/assets/svg/snackbar/alert-triangle.svg?component';
import CheckCircleIcon from '@/assets/svg/snackbar/check-circle.svg?component';
import XCircleIcon from '@/assets/svg/snackbar/x-circle.svg?component';
import XCircleAltIcon from '@/assets/svg/snackbar/x-circle-alt.svg?component';
const snackbarService = new SnackbarService();

const snackbar = computed(() => snackbarService.snackbar);
const snackbarPosition = computed(() => {
  const horizontalPosition = snackbar.value?.horizontalPosition || 'left';
  const verticalPosition = snackbar.value?.verticalPosition || 'bottom';
  return `${verticalPosition} ${horizontalPosition}` as const;
});

const prependIconProps = computed(() => iconBindings(snackbar.value?.prependIcon));
const snackbarPrependIcon = computed(() => renderIcon(snackbar.value?.prependIcon));
const appendIconProps = computed(() => iconBindings(snackbar.value?.appendIcon));
const snackbarAppendIcon = computed(() => renderIcon(snackbar.value?.appendIcon));

const prependIconCss = computed(() => {
  return snackbar.value?.prependIconCss ? snackbar.value?.prependIconCss : 'text-tint-0';
});

const appendIconCss = computed(() => {
  return snackbar.value?.appendIconCss ? snackbar.value?.appendIconCss : 'text-tint-0';
});

function iconBindings(icon?: SnackbarIcon) {
  let bindings: SnackbarIconBinding = {};
  if (icon === 'close') {
    bindings = {
      class: 'cursor-pointer',
      onClick: () => handleVisibilityChange(false),
    };
  }
  return bindings;
}

const iconMap: Record<SnackbarIcon, Component> = {
  close: CloseIcon,
  trash: TrashIcon,
  inform: AlertCircleIcon,
  caution: AlertTriangleIcon,
  success: CheckCircleIcon,
  critical: XCircleIcon,
  pen: PenIcon,
  'critical-alt': XCircleAltIcon,
} as const;

function renderIcon(icon?: SnackbarIcon) {
  if (!icon) return;
  return iconMap[icon];
}

function handleVisibilityChange(value: boolean) {
  snackbar.value.show = value;
  snackbarService.showUndo = false;
}

async function handleUndoAction() {
  const fn = snackbarService.undoAction;
  if (fn) {
    await fn();
    handleVisibilityChange(false);
  }
}
</script>

<template>
  <v-snackbar
    :key="snackbar.timeout"
    :timeout="snackbar.timeout"
    :model-value="snackbar.show"
    :location="snackbarPosition"
    :color="snackbar.color"
    @update:model-value="handleVisibilityChange"
  >
    <div class="flex items-center justify-between space-x-2 p-1">
      <div class="flex items-center space-x-2">
        <div class="mr-1">
          <component
            v-if="snackbarPrependIcon"
            :is="snackbarPrependIcon"
            v-bind="prependIconProps"
            class="h-6 w-6"
            :class="prependIconCss"
          />
        </div>
        <p
          class="snackbar-text"
          :class="snackbar.textCss"
          v-html="snackbar.text"
          data-test-id="snackbar-text"
        ></p>
      </div>
      <div class="flex items-center justify-end space-x-2">
        <span
          v-if="snackbar.showUndo"
          class="cursor-pointer text-sm font-bold"
          :class="appendIconCss"
          data-test-id="snackbar-action"
          @click="handleUndoAction"
        >
          {{ snackbar.actionText || 'UNDO' }}
        </span>
        <component
          v-if="snackbarAppendIcon"
          :is="snackbarAppendIcon"
          v-bind="appendIconProps"
          class="h-6 w-6"
          :class="appendIconCss"
        />
      </div>
    </div>
  </v-snackbar>
</template>
<style>
/* Custom Snackbar border-radius for the whole website*/
.v-snackbar__wrapper {
  @apply !rounded-lg;
}
</style>
